import React, { useEffect, useState, createContext } from "react";
import moment from "moment";
import "moment/locale/es";
import { useSelector, useDispatch } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";
import BarLoader from "react-spinners/BarLoader";
import { IntlProvider } from "react-intl";
import ReactGA from "react-ga";
import PropTypes from "prop-types";
import {
  userLoginByAdminAsync,
  userImpersonateAsync,
  verifyTokenAsync,
} from "./actions/auth-async.action";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  Outlet,
  useLocation,
} from "react-router-dom";
import English from "./translation/en/englishTranslation.json";
import Spanish from "./translation/es/spanishTranslation.json";
import authRoutes from "./routes/auth.route";
import subscriptionsRoutes from "./routes/subscriptions.route";
import FullLayout from "./layouts/fullLayout";
import ThemeRoutes from "./routes/router";
import { setHeadAttributes } from "./utils/common.util";
import { safeReturnPath, takeReturnTo } from "./utils/return-to.util";

export const localeContext = createContext("en");
export const googleResultsContext = createContext({});

const TRACKING_ID = "UA-182103257-1";
ReactGA.initialize(TRACKING_ID);

const AuthWrapper = ({
  component: Component,
  isAuthenticated,
  title = "Delete My Info",
  description = "",
}) => {
  const location = useLocation();

  useEffect(() => {
    setHeadAttributes(title, description);
  }, [location, title, description]);

  if (
    !isAuthenticated ||
    location.pathname.startsWith("/subscriptions") ||
    location.pathname === "/auth/thankyou"
  ) {
    return <Component />;
  }
  // Logged in on an /auth page: go where the visitor was headed before login, if anywhere.
  const target =
    safeReturnPath(location.state?.from) || takeReturnTo() || "/dashboard";
  return <Navigate to={target} replace />;
};

AuthWrapper.propTypes = {
  component: PropTypes.elementType.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
};

export default function App() {
  const auth_obj = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const { authLoading, isAuthenticated, user } = auth_obj;
  const message = { en: English, es: Spanish };
  const [googleSearchResults, setGoogleSearchResults] = useState();
  const [locale, setLocale] = useState();
  moment.locale(`${locale}`);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Signed, single-use grant from the admin app. Preferred, and the only
    // shape that proves a staff member actually authorised this.
    if (params.get("token")) {
      dispatch(userImpersonateAsync(params.get("token")));
      return;
    }

    // Legacy: base64 identifiers in the URL, with nothing signing them. Kept
    // only so an admin build that predates the grant keeps working during the
    // rollout; remove once admin/ is deployed, together with the server's
    // LEGACY_IMPERSONATION variable.
    if (params.get("userId") && params.get("createdBy")) {
      const userId = decodeURIComponent(
        escape(window.atob(params.get("userId"))),
      );
      const createdBy = decodeURIComponent(
        escape(window.atob(params.get("createdBy"))),
      );
      dispatch(userLoginByAdminAsync(userId, createdBy, null, null, true));
      return;
    }
    dispatch(verifyTokenAsync());
  }, [dispatch]);

  useEffect(() => {
    setLocale(user?.language ? user?.language : "en");
  }, [user]);

  if (authLoading) {
    return (
      <div className="vh-100 vw-100 d-flex justify-content-center">
        <BarLoader
          css="margin: auto;"
          size={100}
          color={"#3D4975"}
          loading={authLoading}
        />
      </div>
    );
  } else {
    return (
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <IntlProvider locale={locale} messages={message[locale]}>
          <googleResultsContext.Provider
            value={[googleSearchResults, setGoogleSearchResults]}
          >
            <localeContext.Provider value={[locale, setLocale]}>
              <Router>
                <Routes>
                  <Route path="auth" element={<Outlet />}>
                    {authRoutes.map((prop, key) => {
                      return (
                        <Route
                          key={`route_${key}`}
                          path={prop.path}
                          element={
                            <AuthWrapper
                              isAuthenticated={isAuthenticated}
                              component={prop.component}
                              title={prop.title}
                              description={prop.description}
                            />
                          }
                        />
                      );
                    })}
                  </Route>
                  <Route path="subscriptions" element={<Outlet />}>
                    {subscriptionsRoutes.map((prop, key) => {
                      return (
                        <Route
                          key={`route_${key}`}
                          path={prop.path}
                          element={
                            <AuthWrapper
                              isAuthenticated={isAuthenticated}
                              component={prop.component}
                              title={prop.title}
                              description={prop.description}
                            />
                          }
                        />
                      );
                    })}
                  </Route>
                  <Route path="/" component={<Outlet />}>
                    {ThemeRoutes.map((prop, key) => {
                      return (
                        <Route
                          key={`route_${key}`}
                          path={prop.path}
                          element={
                            <FullLayout
                              component={prop.component}
                              props={prop}
                              isAuthenticated={isAuthenticated}
                              title={prop.title}
                              description={prop.description}
                            />
                          }
                        />
                      );
                    })}
                  </Route>
                </Routes>
              </Router>
            </localeContext.Provider>
          </googleResultsContext.Provider>
        </IntlProvider>
      </GoogleOAuthProvider>
    );
  }
}
